import { AuthConsumerRenderProps, withAuthContext } from "auth/AuthContext";
import { NotificationSystemProps, withNotificationSystemContext } from "context/NotificationSysemContext";
import _ from "lodash";
import * as React from "react";
import { getNewNotifications } from "services/userNotificationServices";
import { Privilege } from "ts-types/api.enums";
import { UserNotificationDto } from "ts-types/api.types";


export interface DocumentState {
  id: number;
  requiresDoctorAction: boolean;
  notifications: UserNotificationDto[];
}

interface State {
  canReceiveDoctorNotifications: () => boolean;
  lastNotificationId: number;
  totalNumberOfDocumentsThatRequireDoctorAction: number;
  documents: DocumentState[];
  refreshNotifications: () => void;
}

interface Props extends NotificationSystemProps,
    AuthConsumerRenderProps {

  children: JSX.Element | Array<JSX.Element>
}

export const UserNotificationContext = React.createContext<State>({
  canReceiveDoctorNotifications: () => false,
  lastNotificationId: 0,
  totalNumberOfDocumentsThatRequireDoctorAction: 0,
  documents: [],
  refreshNotifications: () => {
  }
});

class UserNotificationProviderImpl extends React.Component<Props, State> {

  private refreshIntervalId: number | undefined = undefined;

  constructor(props: Props) {
    super(props);

    this.state = {
      canReceiveDoctorNotifications: this.canReceiveDoctorNotifications,
      lastNotificationId: 0,
      totalNumberOfDocumentsThatRequireDoctorAction: 0,
      documents: [],
      refreshNotifications: this.fetchNotifications
    }

    this.initialize();
  }

  componentWillUnmount() {
    this.refreshIntervalId && clearInterval(this.refreshIntervalId);
  }

  async initialize(): Promise<void> {
    const interval = 5000;
    this.refreshIntervalId = window.setInterval(this.fetchNotifications, interval);
  }

  canReceiveDoctorNotifications = (): boolean => {
    const currentUser = this.props.currentUser;
    const userRoles = currentUser ? currentUser.roles : [];
    const isAppAdmin = userRoles.includes("ROLE_APP_ADMIN");
    return isAppAdmin || (
        !!currentUser
        && this.props.currentUserHasPrivilege(Privilege.PX_CORE_CAN_VIEW_REQUIRES_DOCTOR_ACTION_NOTIFICATIONS)
    );
  }

  fetchNotifications: () => Promise<void> = async () => {

    if (!this.props.currentUser) {
      return;
    }

    if (!this.canReceiveDoctorNotifications()) {
      this.refreshIntervalId && clearInterval(this.refreshIntervalId);
      return;
    }

    const newNotifications = await getNewNotifications(this.state.lastNotificationId);

    let newDocs: DocumentState[] = [];
    let newDocsList = this.state.documents;
    newNotifications.notifications.forEach(notification => {

      const existingDoc = newDocsList.find(
          doc => doc.id === notification.documentId);
      if (existingDoc) {
        existingDoc.notifications.push(notification);
        if (notification.type === "DOC_REQUIRES_DOCTOR_ACTION_SET") {
          existingDoc.requiresDoctorAction = true;
        } else if (notification.type === "DOC_REQUIRES_DOCTOR_ACTION_UNSET") {
          existingDoc.requiresDoctorAction = false;
        } else if (notification.type === "DOC_NOTIFICATION_DELETED") {
          newDocs = newDocs.filter(doc => doc.id !== notification.documentId);
          newDocsList = newDocsList.filter(doc => doc.id !== notification.documentId);
        }
      } else if (notification.type === "DOC_REQUIRES_DOCTOR_ACTION_SET") {
        const newDoc: DocumentState = {
          id: notification.documentId,
          requiresDoctorAction: true,
          notifications: [notification]
        }

        newDocs.push(newDoc);
        newDocsList = [newDoc, ...newDocsList];
      }
    })

    if (!_.isEmpty(newDocs)) {
      const {selectionNotificationSystem} = this.props;

      //@ts-ignore
      const notification = selectionNotificationSystem.current;
      if (notification) {

        const message = newDocs.length === 1
            ? `A document requires doctor action.`
            : `${newDocs.length} documents require doctor action.`;
        notification.addNotification({
          title: "Doctor action required",
          message: message,
          level: 'info'
        });
      }
    }

    this.setState({
      totalNumberOfDocumentsThatRequireDoctorAction: newNotifications.totalNumberOfDocumentsThatRequireDoctorAction,
      documents: newDocsList,
      lastNotificationId: newNotifications.lastNotificationId
    });
  }

  render() {
    return (
        <UserNotificationContext.Provider value={this.state}>
          {this.props.children}
        </UserNotificationContext.Provider>
    );
  }
}

export const UserNotificationProvider = withAuthContext(
    withNotificationSystemContext(
        UserNotificationProviderImpl));

export const UserNotificationConsumer = UserNotificationContext.Consumer;

export interface UserNotificationConsumerRenderProps extends State {
}

export const withUserNotificationContext = (Component: typeof React.Component | React.FunctionComponent<any>) => {
  return (props: any) => (
      <UserNotificationConsumer>
        {({...patientProps}: UserNotificationConsumerRenderProps) => {
          return <Component {...props} {...patientProps} />
        }}
      </UserNotificationConsumer>
  );
};
